import { WORK_ITEM_STATUSES, type WorkItem } from '@deskworks/shared-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ListTodo, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CreateWorkItemDialog } from '@/components/create-work-item-dialog';
import { Page, PageHeader } from '@/components/page';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { SelectableWorkItemList } from '@/components/work-item-bulk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useAgents, useDesks, useWorkspaceId } from '@/lib/hooks';
import { intlLocale, t } from '@/lib/i18n';
import { WORK_ITEM_STATUS } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import type { WorkItemsSearch } from '@/router';

const ALL = '__all__';
const LIMIT = 200;

export function WorkItemsPage() {
  const workspaceId = useWorkspaceId();
  const search = useSearch({ from: '/_authed/w/$workspaceId/work-items' });
  const navigate = useNavigate({ from: '/w/$workspaceId/work-items' });
  const desks = useDesks(workspaceId);
  const agents = useAgents(workspaceId);
  const [q, setQ] = useState(search.q ?? '');

  const setSearch = (patch: Partial<WorkItemsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  // Debounce the free-text search into the URL.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((search.q ?? '') !== q.trim()) setSearch({ q: q.trim() || undefined });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [q]);

  const query = { status: search.status, deskId: search.deskId, agentId: search.agentId, q: search.q, limit: LIMIT };
  const items = useQuery({
    queryKey: qk.workItems(workspaceId, query),
    queryFn: () => api.workItems.list(workspaceId, query),
    placeholderData: keepPreviousData,
  });

  const tree = useMemo(() => buildTree(items.data ?? []), [items.data]);
  const hasFilter = !!(search.status || search.deskId || search.agentId || search.q);

  return (
    <Page wide>
      <PageHeader
        title={t('Work Items')}
        description={t('All concrete tasks in this Workspace. Each Work Item is handled by exactly one agent and ends with a result.')}
        actions={
          <CreateWorkItemDialog workspaceId={workspaceId}>
            <Button>
              <Plus /> {t('Create work item')}
            </Button>
          </CreateWorkItemDialog>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-52 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Search …')} className="h-8 pl-8" />
        </div>
        <FilterSelect
          value={search.status}
          onChange={(v) => setSearch({ status: v })}
          placeholder={t('Status')}
          allLabel={t('All statuses')}
        >
          <SelectItem value="open">{t('Open (everything not done)')}</SelectItem>
          <SelectItem value="needs_approval,waiting,failed">{t('Needs attention')}</SelectItem>
          <SelectSeparator />
          {WORK_ITEM_STATUSES.map((s) => (
            <SelectItem key={s} value={s}>
              {WORK_ITEM_STATUS[s].label}
            </SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect value={search.deskId} onChange={(v) => setSearch({ deskId: v })} placeholder={t('Desk')} allLabel={t('All desks')}>
          {(desks.data ?? []).map((d) => (
            <SelectItem key={d.id} value={d.id}>
              {d.name}
            </SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect value={search.agentId} onChange={(v) => setSearch({ agentId: v })} placeholder={t('Agent')} allLabel={t('All agents')}>
          {(agents.data ?? []).map((a) => (
            <SelectItem key={a.id} value={a.id}>
              {a.name}
            </SelectItem>
          ))}
        </FilterSelect>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQ('');
              void navigate({ search: {}, replace: true });
            }}
          >
            <X /> {t('Reset filters')}
          </Button>
        )}
      </div>

      {items.isPending ? (
        <LoadingRows rows={6} />
      ) : items.isError ? (
        <ErrorState error={items.error} onRetry={() => void items.refetch()} />
      ) : items.data.length === 0 ? (
        <EmptyState
          icon={<ListTodo />}
          title={hasFilter ? t('No matches') : t('No Work Items yet')}
          description={
            hasFilter
              ? t('No Work Items match these filters. Try different filters.')
              : t('Work Items appear when a Desk runs and the Lead Agent finds work — or when you create one yourself.')
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <SelectableWorkItemList
            items={tree.rows.map((r) => r.item)}
            workspaceId={workspaceId}
            row={(item) => {
              const subItems = tree.children.get(item.id)?.length;
              return {
                depth: tree.depth.get(item.id),
                trailing: subItems ? (
                  <span className="hidden text-xs text-muted-foreground lg:inline" title={t('Sub-items')}>
                    +{subItems}
                  </span>
                ) : undefined,
              };
            }}
          />
          <div className="border-t bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
            {items.data.length === 1 ? t('1 Work Item') : t('{n} Work Items', { n: items.data.length.toLocaleString(intlLocale) })}
            {items.data.length >= LIMIT && ` ${t('(the first {n} — use filters to narrow it down)', { n: LIMIT })}`}
            {items.isFetching && ` · ${t('updating …')}`}
          </div>
        </Card>
      )}
    </Page>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  allLabel,
  children,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  placeholder: string;
  allLabel: string;
  children: ReactNode;
}) {
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger size="sm" className="w-auto min-w-36">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {children}
      </SelectContent>
    </Select>
  );
}

function buildTree(items: WorkItem[]) {
  const ids = new Set(items.map((i) => i.id));
  const children = new Map<string, WorkItem[]>();
  const roots: WorkItem[] = [];
  for (const it of items) {
    if (it.parentWorkItemId && ids.has(it.parentWorkItemId)) {
      const list = children.get(it.parentWorkItemId) ?? [];
      list.push(it);
      children.set(it.parentWorkItemId, list);
    } else {
      roots.push(it);
    }
  }
  // In the order shown: every item followed by its sub-items, at most 6 levels deep.
  const rows: { item: WorkItem; depth: number }[] = [];
  const walk = (nodes: WorkItem[], depth: number) => {
    for (const n of nodes) {
      rows.push({ item: n, depth });
      if (depth < 6) walk(children.get(n.id) ?? [], depth + 1);
    }
  };
  walk(roots, 0);
  return { rows, children, depth: new Map(rows.map((r) => [r.item.id, r.depth])) };
}
